import * as THREE from 'three';
import store from '../../redux/store';
import { update_assets_data } from '../../redux/Slice/gridSlice';
import { sceneStateHelper } from './SceneStateHelper';
import { syncHotspotDesignQuaternion } from '../../components/gridpagefunction/HotSpot';
import { getTextPublishPanelScale } from '../../components/gridpagefunction/Text';

const ASSET_TYPES_WITH_TRANSFORMS = [
  'hotspot',
  'mesh',
  'image',
  'video',
  'audio',
  'text',
];

const radToDeg = (rad) => (Number(rad) * 180) / Math.PI;

export function getObjectAssetId(obj) {
  if (!obj) return null;
  return obj.name || obj._id || obj.url || obj.temp_file_name || null;
}

/** Ensure userData can receive new keys (Redux/immer may freeze it). */
export function ensureUserDataExtensible(obj) {
  if (!obj) return;
  if (!obj.userData) {
    obj.userData = {};
    return;
  }
  if (!Object.isExtensible(obj.userData)) {
    obj.userData = { ...obj.userData };
  }
}

/** Set a userData field even when the object was hydrated from frozen Redux state. */
export function setUserDataField(obj, key, value) {
  if (!obj) return;
  try {
    ensureUserDataExtensible(obj);
    obj.userData[key] = value;
  } catch {
    obj.userData = { ...(obj.userData || {}), [key]: value };
  }
}

/**
 * Update a Vector3-like field on userData without assigning read-only properties.
 */
function setUserDataVector3(userData, key, source) {
  const existing = userData[key];
  if (existing && typeof existing.copy === 'function') {
    try {
      existing.copy(source);
      return;
    } catch {
      // frozen vector — fall through to replace via new userData below
    }
  }

  try {
    userData[key] = source.clone();
  } catch {
    // property is read-only; rebuild userData (caller must re-assign on obj)
    throw new Error(`read-only userData.${key}`);
  }
}

function rebuildUserDataWithTransforms(obj) {
  const prev = obj.userData || {};
  const rot = obj.rotation;
  obj.userData = {
    ...prev,
    originalPosition: obj.position.clone(),
    originalRotation: obj.rotation.clone(),
    originalScale: obj.scale.clone(),
    framePosition: obj.position.clone(),
    frameRotation: obj.rotation.clone(),
    frameScale: obj.scale.clone(),
    prevRotation: new THREE.Euler(rot.x, rot.y, rot.z, rot.order),
  };
}

function ensureUserDataEuler(userData, key, sourceEuler) {
  const existing = userData[key];
  if (existing && typeof existing.set === 'function') {
    try {
      existing.set(sourceEuler.x, sourceEuler.y, sourceEuler.z, sourceEuler.order);
      return;
    } catch {
      // fall through
    }
  }
  userData[key] = new THREE.Euler(
    sourceEuler.x,
    sourceEuler.y,
    sourceEuler.z,
    sourceEuler.order
  );
}

/**
 * Keep animation baseline transforms aligned with the current grid pose
 * so preview→design reset does not snap back to an old animated pose.
 */
export function refreshAnimationTransformAnchors(obj) {
  if (!obj) return;

  try {
    ensureUserDataExtensible(obj);
    const ud = obj.userData;

    try {
      // Always re-bind as live Three.js vectors/eulers. History restore can leave
      // plain {x,y,z} objects that break animation (.copy / .set is not a function).
      setUserDataVector3(ud, 'originalPosition', obj.position);
      setUserDataVector3(ud, 'originalRotation', obj.rotation);
      setUserDataVector3(ud, 'originalScale', obj.scale);
      setUserDataVector3(ud, 'framePosition', obj.position);
      setUserDataVector3(ud, 'frameRotation', obj.rotation);
      setUserDataVector3(ud, 'frameScale', obj.scale);
      ensureUserDataEuler(ud, 'prevRotation', obj.rotation);
    } catch {
      rebuildUserDataWithTransforms(obj);
    }
  } catch {
    try {
      rebuildUserDataWithTransforms(obj);
    } catch {
      // Non-fatal: Redux/scene transform sync can still proceed
    }
  }
}

/**
 * Persist the object's current transform into Redux assets_data (hotspot, mesh, etc.).
 */
export function syncObjectTransformToRedux(obj) {
  if (!obj) return;
  const type = obj.deleteType;
  if (!ASSET_TYPES_WITH_TRANSFORMS.includes(type)) return;

  const objectId = getObjectAssetId(obj);
  if (!objectId) return;

  const state = store.getState();
  const list = state.assets_object?.assets_data?.[type];
  if (!Array.isArray(list) || list.length === 0) return;

  const transformFields = {
    position_x: obj.position.x,
    position_y: obj.position.y,
    position_z: obj.position.z,
    rotation_x: radToDeg(obj.rotation.x),
    rotation_y: radToDeg(obj.rotation.y),
    rotation_z: radToDeg(obj.rotation.z),
    scale_x: obj.scale.x,
    scale_y: obj.scale.y,
    scale_z: obj.scale.z,
  };

  if (type === 'text') {
    const panelScale = getTextPublishPanelScale(obj, obj.scale);
    transformFields.scale_x = panelScale.x;
    transformFields.scale_y = panelScale.y;
    transformFields.scale_z = panelScale.z;
  } else if (type === 'image') {
    const planeW = obj.geometry?.parameters?.width || 1;
    const planeH = obj.geometry?.parameters?.height || 1;
    transformFields.scale_x = planeW * obj.scale.x;
    transformFields.scale_y = planeH * obj.scale.y;
  }

  let changed = false;
  const updated = list.map((asset) => {
    const assetId =
      asset._id ?? asset.id ?? asset.url ?? asset.name ?? asset.temp_file_name;
    if (assetId !== objectId) return asset;
    changed = true;
    return { ...asset, ...transformFields };
  });

  if (changed) {
    store.dispatch(update_assets_data({ type, data: updated }));
  }
}

/**
 * Call after any transform edit in design mode (gizmo or numeric inputs).
 */
export function applyGridTransformUpdate(obj) {
  if (!obj) return;
  if (obj.userData?.isHotspot || obj.deleteType === 'hotspot') {
    syncHotspotDesignQuaternion(obj);
  }
  refreshAnimationTransformAnchors(obj);
  syncObjectTransformToRedux(obj);
  sceneStateHelper.updateObjectState(obj);
}
